import Link from "@web-ui/adapters/link";
import IconDisc from "@web-ui/components/IconDisc";
import type { Tone } from "@web-ui/types/tone";

/*
 * ListRow: one row of a hairline list (devices, members, activity, protections), owner 2026-10-13. A
 * leading icon disc (or any `leading` node: an avatar, a status dot), the title over a quiet detail,
 * `meta` (a date, a chip) and `actions` (buttons) at the end edge. With `href` or `onClick` the title is
 * the control and stretches over the whole row, the actions stay their own controls above it, and the
 * row presses (motion-press). Rows wrap on a phone: the text keeps 12rem before the end items drop
 * under it. Put rows in a `motion-stagger` container to let them arrive one after the other. Styles in
 * styles/data-display.css .list-row.
 */
type ListRowProps = {
    className?: string;
    /** A kit icon name, drawn in an IconDisc. */
    icon?: string;
    /** The disc's tone (neutral by default). */
    tone?: Tone;
    /** Any leading node instead of the icon disc (an Avatar, a StatusDot). */
    leading?: React.ReactNode;
    title: React.ReactNode;
    detail?: React.ReactNode;
    /** Quiet text or a chip at the end edge. */
    meta?: React.ReactNode;
    /** Buttons at the end edge. */
    actions?: React.ReactNode;
    href?: string;
    onClick?: () => void;
    selected?: boolean;
};

const ListRow = ({ className, icon, tone = "neutral", leading, title, detail, meta, actions, href, onClick, selected }: ListRowProps) => {
    const interactive = Boolean(href || onClick);
    const heading = href ? (
        <Link className="list-row__control" href={href} onClick={onClick} aria-current={selected ? "true" : undefined}>
            {title}
        </Link>
    ) : onClick ? (
        <button type="button" className="list-row__control" onClick={onClick} aria-pressed={selected === undefined ? undefined : selected}>
            {title}
        </button>
    ) : (
        title
    );
    return (
        <div
            className={`list-row ${interactive ? "list-row--interactive motion-press" : ""} ${className || ""}`}
            data-selected={selected || undefined}
        >
            {icon ? <IconDisc className="list-row__lead" tone={tone} icon={icon} /> : leading ? <span className="list-row__lead">{leading}</span> : null}
            <div className="list-row__main">
                <div className="list-row__title">{heading}</div>
                {detail ? <div className="list-row__detail">{detail}</div> : null}
            </div>
            {meta ? <div className="list-row__meta">{meta}</div> : null}
            {actions ? <div className="list-row__actions">{actions}</div> : null}
        </div>
    );
};

export default ListRow;
